// Partner API keys — issue once, see status, revoke instantly.
//
// The screen is shaped around the one fact that cannot be worked around: a key
// is readable exactly once, in the response that created it. So the reveal is
// deliberately obtrusive (a panel that will not tidy itself away), the copy
// button is the primary action, and dismissing it says plainly that the value
// is gone for good. Everything else on the page is status.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Copy, Check, ShieldOff, AlertTriangle } from 'lucide-react'
import { toast } from '../../lib/toast'
import { API_BASE } from '../../lib/api'
import { PartnerApiSwitch } from './PartnerApiSwitch'
import { formatInstant } from '../../lib/datetime'
import { useT } from '../../i18n'
import { partnerApiI18n } from '../../i18n/admin/partner-api'
import { DataTableShell } from '../../components/ui/data-table-shell'

type PartnerApiClient = {
  id: string
  org_id: string
  org_name: string
  name: string
  key_prefix: string
  scopes: string[]
  active: boolean
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string
  shared_product_count: number
}

type IssuedKey = { client: PartnerApiClient; secret: string }

type PartnerOrg = { id: string; name: string; org_type: string }

function formatWhen(value: string | null): string {
  // Through lib/datetime, never Intl directly: the whole app renders instants in
  // one timezone and one format, and a screen that quietly picks the viewer's
  // locale would disagree with every other timestamp on it.
  if (!value) return '—'
  return formatInstant(value)
}

/** The one-time reveal. Cannot be recovered once dismissed, and says so. */
function IssuedKeyPanel({ issued, onDone }: { issued: IssuedKey; onDone: () => void }) {
  const t = useT(partnerApiI18n).keys
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(issued.secret)
      setCopied(true)
      toast.success(t.keyCopied)
    } catch {
      // Clipboard can be blocked; the value is on screen either way, so this is
      // a convenience failing, not the operation failing.
      toast.error(t.copyFailed)
    }
  }

  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="text-sm text-amber-900">
          <div className="font-medium">{t.revealTitle}</div>
          <div className="mt-0.5 text-amber-800">
            {t.revealBody}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <code className="flex-1 select-all break-all rounded border border-amber-200 bg-white px-3 py-2 font-mono text-xs">
          {issued.secret}
        </code>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-700"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? t.copied : t.copy}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] text-amber-800">
          {t.sendHint}
        </span>
        <button
          type="button"
          onClick={onDone}
          className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100"
        >
          {t.doneCopying}
        </button>
      </div>
    </div>
  )
}

export function PartnerApiKeys() {
  const t = useT(partnerApiI18n).keys
  const queryClient = useQueryClient()
  const [issued, setIssued] = useState<IssuedKey | null>(null)
  const [orgId, setOrgId] = useState('')
  const [name, setName] = useState('')
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  const clientsQuery = useQuery({
    queryKey: ['admin-partner-api-clients'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/partner-api-clients`)
      if (!res.ok) throw new Error(`${res.status}`)
      return (await res.json()) as { items: PartnerApiClient[] }
    },
  })

  // Partner organisations are BO3. The key binds to one, and the catalog it can
  // read is whatever that organisation has been assigned — so choosing the wrong
  // one here is the mistake worth preventing.
  const orgsQuery = useQuery({
    queryKey: ['admin-partner-orgs'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/orgs?org_type=BO3&limit=200`)
      if (!res.ok) throw new Error(`${res.status}`)
      const body = (await res.json()) as { items: PartnerOrg[] }
      return body.items ?? []
    },
  })

  const issueMutation = useMutation({
    mutationFn: async (vars: { orgId: string; name: string }) => {
      const res = await fetch(`${API_BASE}/admin/partner-api-clients`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ org_id: vars.orgId, name: vars.name }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return (await res.json()) as IssuedKey
    },
    onSuccess: (data) => {
      setIssued(data)
      setName('')
      void queryClient.invalidateQueries({ queryKey: ['admin-partner-api-clients'] })
    },
    onError: () => toast.error(t.issueFailed),
  })

  const revokeMutation = useMutation({
    mutationFn: async (clientId: string) => {
      const res = await fetch(`${API_BASE}/admin/partner-api-clients/${clientId}/revoke`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      toast.success(t.revoked)
      setConfirmRevoke(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-partner-api-clients'] })
    },
    onError: () => toast.error(t.revokeFailed),
  })

  const rows = useMemo(() => clientsQuery.data?.items ?? [], [clientsQuery.data])
  const canIssue = orgId !== '' && name.trim() !== '' && !issueMutation.isPending

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
          <KeyRound className="h-4 w-4" />
          {t.title}
        </h2>
        <p className="text-sm text-slate-500">
          {t.description}
        </p>
        {/* The page a partner's developer builds against. Sent with the key, and
            linked here so it can be found again without hunting for the file. */}
        <p className="text-sm">
          <a
            href="/partner-api/"
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 underline underline-offset-2 hover:text-indigo-700"
          >
            {t.guideLink}
          </a>
          <span className="ml-2 text-slate-400">
            {t.guideHint}
          </span>
        </p>
      </header>

      <PartnerApiSwitch
        activeKeyCount={rows.filter((row) => row.active).length}
        sharedProductCount={rows.reduce((sum, row) => sum + row.shared_product_count, 0)}
      />

      {issued && <IssuedKeyPanel issued={issued} onDone={() => setIssued(null)} />}

      <section className="rounded border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-medium text-slate-800">{t.issueHeading}</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              {t.partner}
            </span>
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="min-w-[220px] rounded border border-slate-300 px-2 py-1.5 text-sm"
            >
              <option value="">{t.chooseOrg}</option>
              {orgsQuery.data?.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">{t.label}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.labelPlaceholder}
              className="min-w-[220px] rounded border border-slate-300 px-2 py-1.5 text-sm"
            />
          </label>
          <button
            type="button"
            disabled={!canIssue}
            onClick={() => issueMutation.mutate({ orgId, name })}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
          >
            {issueMutation.isPending ? t.issuing : t.issueKey}
          </button>
        </div>
        <p className="text-[11px] text-slate-500">
          {t.labelHint}
        </p>
      </section>

      <DataTableShell>
        <table>
          <thead>
            <tr>
              <th className="py-2">{t.columns.partner}</th>
              <th className="py-2">{t.columns.label}</th>
              <th className="py-2">{t.columns.key}</th>
              <th className="py-2 text-right">{t.columns.productsShared}</th>
              <th className="py-2">{t.columns.lastUsed}</th>
              <th className="py-2">{t.columns.status}</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-sm text-[var(--color-text-muted)]">
                  {t.empty}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="py-2 text-[var(--color-text)]">{row.org_name}</td>
                <td className="py-2 text-[var(--color-text-muted)]">{row.name}</td>
                <td className="py-2 font-mono text-xs text-[var(--color-text-muted)]">
                  {row.key_prefix}…
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                  {row.shared_product_count}
                </td>
                <td className="py-2 text-[var(--color-text-muted)]">{formatWhen(row.last_used_at)}</td>
                <td className="py-2">
                  {row.active ? (
                    <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700 ring-1 ring-emerald-200">
                      {t.statusActive}
                    </span>
                  ) : (
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                      {row.revoked_at ? t.statusRevoked : t.statusExpired}
                    </span>
                  )}
                </td>
                <td className="py-2 text-right">
                  {row.active &&
                    (confirmRevoke === row.id ? (
                      <span className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          disabled={revokeMutation.isPending}
                          onClick={() => revokeMutation.mutate(row.id)}
                          className="rounded bg-rose-600 px-2 py-1 text-xs text-white hover:bg-rose-700 disabled:bg-slate-200 disabled:text-slate-500"
                        >
                          {t.revokeForGood}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmRevoke(null)}
                          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600"
                        >
                          {t.cancel}
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRevoke(row.id)}
                        title={t.revokeTitle}
                        className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                      >
                        <ShieldOff className="h-3.5 w-3.5" />
                        {t.revoke}
                      </button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableShell>

      <p className="text-[11px] text-slate-500">
        {t.footnote}
      </p>
    </div>
  )
}
